import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { pushEvent } from "@/lib/analytics";
import {
  addressFromQuery, attributionPayload, captureAttribution, groupSlotsByDate,
  resolveAddress, sendStartEvent, sendVisitBeacon, THC,
} from "@/lib/tankHealthCheck";
import { CampaignHeader } from "@/components/tank-health-check/CampaignHeader";
import { HealthCheckHero } from "@/components/tank-health-check/HealthCheckHero";
import { OwnerBanner, TrustStrip } from "@/components/tank-health-check/TrustStrip";
import { WhatWeCheck } from "@/components/tank-health-check/WhatWeCheck";
import {
  BonusPanel, IncludesPanel, QualificationForm,
} from "@/components/tank-health-check/QualificationForm";
import { BookingPanel } from "@/components/tank-health-check/BookingPanel";
import { ReferralPanel } from "@/components/tank-health-check/ReferralPanel";
import { CampaignFooter } from "@/components/tank-health-check/CampaignFooter";

export default function TankWaterHealthCheck() {
  const { search } = useLocation();
  const navigate = useNavigate();
  useSEO({
    title: "Complimentary Tank Water Health Check | AWARE Water Testing",
    description: "Book a Complimentary Tank Water Health Check on the Sunshine Coast. "
      + "Owner must be present. First 10 eligible bookings receive 20% off any AWARE lab test package.",
    noindex: true,
  });

  const [address, setAddress] = useState(() => addressFromQuery(search));
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", mobile: "" });
  const [answers, setAnswers] = useState({});
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  // Jun 2026 — territory decision. `null` until the server answers for a
  // usable address; drives which completion section renders.
  const [regionInfo, setRegionInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [autocompleteLive, setAutocompleteLive] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    captureAttribution(search);
    sendVisitBeacon(search, "/tank-water-health-check");
  }, [search]);

  // Jun 2026 — server decides the territory (never the browser). Runs on
  // every address change; a partner-referral property never triggers the
  // slot API below.
  useEffect(() => {
    const params = new URLSearchParams();
    if (address.postcode) params.set("postcode", address.postcode);
    if (address.suburb) params.set("suburb", address.suburb);
    if (address.property_address) params.set("address", address.property_address);
    if (!params.toString()) { setRegionInfo(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/tank-water-health-check/service-region?${params.toString()}`)
        .then(({ data }) => { if (!cancelled) setRegionInfo(data); })
        .catch(() => { if (!cancelled) setRegionInfo(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [address.postcode, address.suburb, address.property_address]);

  const regionStatus = regionInfo?.service_region_status || null;
  // Only a confirmed DIRECT-service property gets the appointment calendar.
  const partnerReferral = regionStatus === "partner_referral" || regionStatus === "unresolved";

  // Jun 2026 — GEO-aware availability. The property's coordinates (from
  // Google Places, or its postcode/suburb as a fallback) let the server
  // rank every eligible technician's route and return only the most
  // efficient times. Without them the server falls back to the plain list.
  const slotParams = useCallback(() => {
    const p = new URLSearchParams();
    if (address.latitude && address.longitude) {
      p.set("lat", address.latitude);
      p.set("lng", address.longitude);
    }
    if (address.postcode) p.set("postcode", address.postcode);
    if (address.suburb) p.set("suburb", address.suburb);
    if (address.state) p.set("state", address.state);
    const qs = p.toString();
    return `/tank-water-health-check/slots${qs ? `?${qs}` : ""}`;
  }, [address.latitude, address.longitude, address.postcode, address.suburb, address.state]);

  useEffect(() => {
    // Hard rule: the slot API is never called for a referral property, and
    // never while an entered address is still awaiting its decision.
    if (partnerReferral) { setSlots([]); setLoadingSlots(false); return; }
    if (address.property_address && regionStatus === null) return;
    let cancelled = false;
    api.get(slotParams())
      .then(({ data }) => { if (!cancelled) setSlots(data.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [partnerReferral, regionStatus, address.property_address, slotParams]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 420);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const startFired = useRef(false);

  /** A Health Check START — the customer's first real interaction with the
      booking flow. Page load alone is never a start. */
  const markStart = useCallback((trigger) => {
    if (startFired.current) return;
    startFired.current = true;
    sendStartEvent(search, trigger);
  }, [search]);

  const days = useMemo(() => groupSlotsByDate(slots), [slots]);
  const ownerBlocked = answers.owner_present === "no";
  // Town-water-only properties are outside the campaign — booking times stay
  // locked, exactly like the owner-present gate.
  const sourceBlocked = answers.water_source === "none";
  const locked = answers.owner_present !== "yes" || sourceBlocked;

  const patchAddress = useCallback((patch) => {
    markStart("property_address");
    setAddress((a) => ({ ...a, ...patch }));
  }, [markStart]);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const goToBooking = useCallback(() => {
    scrollTo(ownerBlocked || sourceBlocked ? "thc-owner-gate" : locked ? "how-it-works" : "book");
  }, [ownerBlocked, sourceBlocked, locked, scrollTo]);

  const submit = async () => {
    setError("");
    if (!address.property_address.trim()) {
      setError("Please enter your property address.");
      return scrollTo("thc-hero");
    }
    if (!address.address_verified && autocompleteLive) {
      setError("Please select your property address from the suggestions.");
      return scrollTo("thc-hero");
    }
    if (!selectedSlot) {
      setError("Please choose an appointment date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const resolved = resolveAddress({
        ...address,
        address_source: address.address_source
          || (autocompleteLive ? "typed" : "typed_google_unavailable"),
      });   // fills gaps in a merged address
      const { data } = await api.post("/tank-water-health-check/bookings", {
        ...contact,
        ...resolved,
        ...answers,
        ...attributionPayload(),
        slot_id: selectedSlot.id,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        landing_page: "/tank-water-health-check",
      });
      const booking = data.booking;
      // Meta Schedule is mapped from this GTM event — fired ONCE, only
      // after the backend has saved the booking. The base Pixel already
      // lives in GTM (dataset 1550539903491839); nothing is added here.
      if (!firedRef.current) {
        firedRef.current = true;
        pushEvent({
          event: "tank_water_health_check_booked",
          booking_type: "complimentary_tank_water_health_check",
          content_name: "Complimentary Tank Water Health Check",
          content_category: "Tank Water Health Check",
          owner_present: true,
          lab_bonus_eligible: !!booking.lab_bonus_eligible,
        });
      }
      // Navigate by reference only — router state would push the booking
      // (including its offer token) into GTM's history-change payload.
      navigate(`/tank-water-health-check/confirmed?ref=${encodeURIComponent(booking.reference)}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Any of these mean the chosen time no longer survives a live
      // re-check — the customer gets fresh times, never a silent failure.
      const taken = ["slot_full", "slot_unavailable", "slot_busy", "time_taken",
        "technician_unavailable", "tech_daily_limit_reached"].includes(detail);
      setError(detail === "water_source_ineligible"
        ? "The complimentary Health Check is for properties relying on rainwater and/or bore water."
        : detail === "owner_present_required"
        ? "The property owner must be present — please change your answer to question 7."
        : detail === "already_booked"
          ? "This property already has a Health Check booked. Please call 5327 3473 if you need to change it."
          : taken
            ? "That time was just taken. Please choose another time."
            : detail || "We could not save your booking. Please try again.");
      if (taken) {
        const { data } = await api.get(slotParams());
        setSlots(data.slots || []);
        setSelectedSlot(null);
      }
    } finally { setSubmitting(false); }
  };

  /** Jun 2026 — out-of-region enquiry. No slot, no appointment, no ServiceM8. */
  const submitReferral = async () => {
    setError("");
    if (!address.property_address.trim()) {
      setError("Please enter your property address.");
      return scrollTo("thc-hero");
    }
    setSubmitting(true);
    try {
      const resolved = resolveAddress({
        ...address,
        address_source: address.address_source
          || (autocompleteLive ? "typed" : "typed_google_unavailable"),
      });
      const { data } = await api.post("/tank-water-health-check/referrals", {
        ...contact,
        ...resolved,
        ...answers,
        ...attributionPayload(),
        notes: contact.notes || "",
        landing_page: "/tank-water-health-check",
      });
      const ref = data.referral;
      if (!firedRef.current) {
        firedRef.current = true;
        pushEvent({
          event: "tank_water_health_check_referral_submitted",
          booking_type: "partner_referral_enquiry",
          content_name: "Tank Water Health Check Partner Referral",
          content_category: "Tank Water Health Check",
          referral_status: ref.referral_status,
          partner_assigned: !ref.partner_pending,
        });
      }
      navigate(`/tank-water-health-check/enquiry-received?ref=${encodeURIComponent(ref.reference)}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(detail === "direct_service_bookable"
        ? "Good news — your property is inside our direct service area. Please choose an appointment time instead."
        : typeof detail === "string" ? detail
          : "We could not submit your enquiry. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ backgroundColor: "#FFFFFF", color: THC.body }} data-testid="thc-page">
      <CampaignHeader onBook={goToBooking} onNav={scrollTo} />
      <HealthCheckHero address={{ ...address, autocomplete_live: autocompleteLive,
        // Jun 2026 — for a referral property the "pick a suggestion" hint
        // is noise (no slot to protect); direct-service bookings still
        // need a Google-verified address, so the hint stays for them.
        region_decided: partnerReferral }}
        onAddressChange={patchAddress}
        onAutocompleteAvailability={setAutocompleteLive}
        onEligibility={() => scrollTo("how-it-works")} onBook={goToBooking} />
      <TrustStrip />
      <OwnerBanner />
      <WhatWeCheck />

      <section id="how-it-works" className="mx-auto max-w-[1440px] px-[18px] md:px-7 xl:px-10 pt-9 md:pt-14">
        <div className="lg:grid lg:grid-cols-[minmax(0,60%)_minmax(0,40%)] lg:gap-7">
          <div id="thc-owner-gate">
            <QualificationForm answers={answers} ownerBlocked={ownerBlocked}
              sourceBlocked={sourceBlocked}
              onAnswer={(field, value) => {
                markStart("qualification_answer");
                setAnswers((a) => ({ ...a, [field]: value }));
                if ((field === "owner_present" && value === "yes")
                  || (field === "water_source" && value !== "none")) setError("");
              }}
              onChangeAnswer={(field = "owner_present") => {
                setAnswers((a) => ({ ...a, [field]: undefined }));
                document.querySelector(`[data-testid="thc-q-${field}"]`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }} />
          </div>
          <div className="mt-5 lg:mt-0 space-y-4">
            <BonusPanel />
            <IncludesPanel />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1440px] px-[18px] md:px-7 xl:px-10 pb-10">
        {/* Jun 2026 — availability wording that invites everyone to complete
            the form, then either the calendar or the referral enquiry. */}
        <p className="mt-9 md:mt-14 text-[15px] md:text-base leading-[1.55] max-w-[900px]"
          style={{ color: THC.muted }} data-testid="thc-service-area-note">
          Complimentary onsite Health Checks are available in selected service areas.
          If your property is outside our direct service region, you can still submit
          your enquiry and we&rsquo;ll help connect you with a Pristine Water Systems
          Partner who services your area.
        </p>
        {partnerReferral ? (
          <ReferralPanel
            contact={contact} onContact={(patch) => {
              markStart("contact_details");
              setContact((c) => ({ ...c, ...patch }));
            }}
            address={address} onEditAddress={() => {
              patchAddress({ address_verified: false });
              scrollTo("thc-hero");
              setTimeout(() => document.querySelector('[data-testid="thc-address-input"]')?.focus(), 400);
            }}
            locked={locked} submitting={submitting} onSubmit={submitReferral}
            error={error} unresolved={regionStatus === "unresolved"} />
        ) : (
          <BookingPanel days={days} selectedDate={selectedDate}
            onDate={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
            selectedSlot={selectedSlot} onSlot={setSelectedSlot}
            contact={contact} onContact={(patch) => {
              markStart("contact_details");
              setContact((c) => ({ ...c, ...patch }));
            }}
            address={address} onEditAddress={() => {
              patchAddress({ address_verified: false });
              scrollTo("thc-hero");
              setTimeout(() => document.querySelector('[data-testid="thc-address-input"]')?.focus(), 400);
            }}
            locked={locked} submitting={submitting} onSubmit={submit} error={error}
            loadingSlots={loadingSlots} />
        )}
      </div>

      <CampaignFooter />

      {scrolled && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 p-2.5 bg-white/95 backdrop-blur border-t"
          style={{ borderColor: THC.border }} data-testid="thc-sticky-cta-bar">
          <button onClick={goToBooking} data-testid="thc-sticky-cta"
            className="w-full h-[54px] rounded-[8px] text-white text-[16px] font-bold"
            style={{ backgroundColor: THC.blue }}>
            {partnerReferral ? "SUBMIT MY ENQUIRY" : "BOOK MY HEALTH CHECK"}
          </button>
        </div>
      )}
    </div>
  );
}
